'use client';

import NumberFlow, { continuous } from '@number-flow/react';
import { AnimatePresence, motion } from 'motion/react';
import { useLayoutEffect, useRef, useState } from 'react';

import { IslandFeedText } from './IslandFeed';

import Button from '@/components/v4/Button';
import Image from '@/components/v2/Image';
import Modal from '@/components/v4/Modal/Modal';
import SummaryChip from '@/components/v4/SummaryChip';
import { TextShimmer } from '@/components/v4/TextShimmer';
import XIcon from '@/icons/XIcon';

const MONG_SRC = '/img2/batch/batch_mong.png';

export type DesktopProgressData = {
  total: number;
  pending: number;
  completed: number;
  failed: number;
  isCompleted: boolean;
  // job 전체가 terminal-non-COMPLETED(FAILED/CANCELLED/EXPIRED) — "등록 중 오류" 상태
  isFailed: boolean;
};

type DesktopProgressViewProps = {
  data: DesktopProgressData;
  expanded: boolean;
  onExpand: () => void;
  onCollapse: () => void;
  onConfirm: () => void;
};

const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));

const DesktopProgressView = ({
  data,
  expanded,
  onExpand,
  onCollapse,
  onConfirm,
}: DesktopProgressViewProps) => {
  const { total, pending, completed, failed, isCompleted, isFailed } = data;
  const done = completed + failed;
  const progress = total > 0 ? (done / total) * 100 : 0;

  if (!expanded) {
    return (
      <FloatingWidget
        isCompleted={isCompleted}
        isFailed={isFailed}
        progress={progress}
        completed={completed}
        failed={failed}
        onExpand={onExpand}
      />
    );
  }

  // terminal(완료/오류)은 닫기=confirm(dismiss), 진행 중은 접기(onCollapse)
  const isTerminal = isCompleted || isFailed;

  return (
    <Modal isOpen close={isTerminal ? onConfirm : onCollapse} direction="center">
      {isCompleted ? (
        <CompletedCard total={total} completed={completed} failed={failed} onConfirm={onConfirm} />
      ) : isFailed ? (
        <FailedCard total={total} completed={completed} failed={failed} onConfirm={onConfirm} />
      ) : (
        <RunningCard
          total={total}
          pending={pending}
          completed={completed}
          failed={failed}
          progress={progress}
          onCollapse={onCollapse}
        />
      )}
    </Modal>
  );
};

const RunningCard = ({
  total,
  pending,
  completed,
  failed,
  progress,
  onCollapse,
}: {
  total: number;
  pending: number;
  completed: number;
  failed: number;
  progress: number;
  onCollapse: () => void;
}) => {
  return (
    <div className="relative w-[600px] rounded-[30px] bg-white pt-[30px]">
      <button type="button" onClick={onCollapse} className="absolute right-7 top-7">
        <XIcon className="size-[34px] text-gray-3" />
      </button>

      <div className="flex flex-col items-center gap-9 px-[28px]">
        <div className="flex w-full flex-col items-center gap-3.5">
          <p className="text-[18px] font-semibold leading-none text-cds-coolgray-90">
            AI 일괄 댓글 등록 중
          </p>

          <div className="text-center text-[16px] text-cds-coolgray-65">
            <p className="mb-0 leading-[1.5]">
              미답변된 <b className="font-semibold text-cds-coolgray-80">{total}개</b>의 리뷰에 대해
              일괄 댓글을 등록하고 있어요
            </p>
            <TextShimmer
              duration={2}
              className="leading-[1.5] font-semibold [--base-color:#ff5e00] [--base-gradient-color:#ffd5b8]"
            >
              이 창을 닫고 다른 매장 업무를 보고 오셔도 좋아요!
            </TextShimmer>
          </div>

          <div className="flex items-center justify-center gap-2.5">
            <SummaryChip label="총" count={total} color="gray" />
            <SummaryChip label="등록중" count={pending} color="blue" />
            <SummaryChip label="성공" count={completed} color="green" />
            <SummaryChip label="실패" count={failed} color="red" />
          </div>
        </div>

        <div className="w-full rounded-[20px] border border-cds-coolgray-20 px-6 pb-5 pt-8">
          <ProgressTrack progress={progress} />
        </div>
      </div>

      <div className="mt-9 flex justify-end border-t border-cds-coolgray-15 px-[30px] pb-6 pt-[18px]">
        <Button
          variant="contained"
          color="coolgray"
          height="44"
          justify="center"
          onClick={onCollapse}
          className="w-[110px] text-[16px]"
        >
          접어두기
        </Button>
      </div>
    </div>
  );
};

// 몽·말풍선을 한 래퍼로 묶어 fill 끝(progress%)에 붙임 — 몽이 96%에 멈추는 desync/역행 방지
const ProgressTrack = ({ progress }: { progress: number }) => {
  const pct = clamp(progress, 0, 100);
  const isStart = pct <= 0;

  // 몽은 fill 끝을 따라가되 말풍선만 트랙 안으로 clamp(끝에서 오른쪽 잘림 방지). shift로 뿔을 되돌려 fill 지점을 계속 가리킴
  const barRef = useRef<HTMLDivElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const [shift, setShift] = useState(0);

  useLayoutEffect(() => {
    if (isStart) {
      setShift(0);
      return;
    }
    const bar = barRef.current;
    const tip = tipRef.current;
    if (!bar || !tip) return;
    const barW = bar.clientWidth;
    const half = tip.offsetWidth / 2;
    const fillPx = (pct / 100) * barW;
    const center = Math.min(Math.max(fillPx, half), Math.max(half, barW - half));
    setShift(center - fillPx);
  }, [pct, isStart]);

  return (
    <div className="relative w-full pt-[58px]">
      <div ref={barRef} className="relative h-[25px] w-full rounded-full bg-cds-coolgray-10">
        <div
          className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-[#6b91f9] to-cds-blue-50 transition-[width] duration-500 ease-out"
          style={{ width: `${pct}%` }}
        />
        <div
          className="absolute top-1/2 z-10 transition-[left] duration-500 ease-out"
          style={{ left: `${pct}%`, transform: 'translate(-50%, -50%)' }}
        >
          {/* bounce와 transform 충돌 방지 위해 위치/애니메이션 래퍼 분리 */}
          <div
            ref={tipRef}
            className="absolute bottom-[calc(100%+16px)] transition-transform duration-500 ease-out"
            style={{
              left: '50%',
              transform: isStart ? 'translateX(0)' : `translateX(calc(-50% + ${shift}px))`,
            }}
          >
            <div className="relative whitespace-nowrap rounded-[8px] bg-cds-blue-50 px-3 py-2 text-[14px] leading-none text-white animate-fa-bounce">
              {isStart ? (
                <>
                  이제 막 등록이 <b className="font-semibold">시작되었어요</b>
                </>
              ) : (
                <>
                  등록중{' '}
                  <b className="font-semibold tabular-nums">
                    <NumberFlow
                      willChange
                      plugins={[continuous]}
                      value={Math.round(pct)}
                      suffix="%"
                    />
                  </b>{' '}
                  완료
                </>
              )}
              <span
                className="absolute -bottom-[5px] size-3 rotate-45 rounded-[2px] bg-cds-blue-50"
                style={{
                  left: isStart ? '12px' : '50%',
                  transform: isStart ? 'none' : `translateX(calc(-50% - ${shift}px))`,
                }}
              />
            </div>
          </div>
          <Image src={MONG_SRC} width={48} height={45} alt="일괄등록 몽" />
        </div>
      </div>
    </div>
  );
};

const CompletedCard = ({
  total,
  completed,
  failed,
  onConfirm,
}: {
  total: number;
  completed: number;
  failed: number;
  onConfirm: () => void;
}) => {
  return (
    <div className="flex w-[392px] flex-col items-center gap-[30px] rounded-[30px] bg-white px-[26px] pb-7 pt-[30px]">
      <div className="flex w-[340px] flex-col items-center gap-3.5">
        <p className="text-[18px] font-semibold leading-none text-cds-coolgray-90">
          AI 일괄 댓글 등록 완료
        </p>

        <div className="text-center text-[16px] text-cds-coolgray-65">
          <p className="mb-0 leading-[1.5]">
            미답변된 <b className="font-semibold text-cds-coolgray-80">{total}개</b>의 리뷰 등록이
            완료되었어요
          </p>
          <p className="leading-[1.5]">등록 실패한 리뷰가 있다면 다시 등록해주세요</p>
        </div>

        <div className="flex items-center justify-center gap-2.5">
          <SummaryChip label="총" count={total} color="gray" />
          <SummaryChip label="성공" count={completed} color="green" />
          <SummaryChip label="실패" count={failed} color="red" />
        </div>
      </div>

      <Button
        variant="contained"
        color="coolgray"
        height="44"
        justify="center"
        onClick={onConfirm}
        className="w-[130px] text-[16px]"
      >
        닫기
      </Button>
    </div>
  );
};

// job 전체 실패(FAILED/CANCELLED/EXPIRED) — "진행중"으로 멈춰 보이지 않게 명시적 오류 상태로 닫기 제공
const FailedCard = ({
  total,
  completed,
  failed,
  onConfirm,
}: {
  total: number;
  completed: number;
  failed: number;
  onConfirm: () => void;
}) => {
  return (
    <div className="flex w-[392px] flex-col items-center gap-[30px] rounded-[30px] bg-white px-[26px] pb-7 pt-[30px]">
      <div className="flex w-[340px] flex-col items-center gap-3.5">
        <p className="text-[18px] font-semibold leading-none text-cds-coolgray-90">
          AI 일괄 댓글 등록 중 오류가 발생했어요
        </p>

        <div className="text-center text-[16px] text-cds-coolgray-65">
          <p className="mb-0 leading-[1.5]">일괄 댓글 등록을 완료하지 못했어요.</p>
          <p className="leading-[1.5]">잠시 후 다시 시도해주세요.</p>
        </div>

        {total > 0 && (
          <div className="flex items-center justify-center gap-2.5">
            <SummaryChip label="총" count={total} color="gray" />
            <SummaryChip label="성공" count={completed} color="green" />
            <SummaryChip label="실패" count={failed} color="red" />
          </div>
        )}
      </div>

      <Button
        variant="contained"
        color="coolgray"
        height="44"
        justify="center"
        onClick={onConfirm}
        className="w-[130px] text-[16px]"
      >
        닫기
      </Button>
    </div>
  );
};

const FloatingWidget = ({
  isCompleted,
  isFailed,
  progress,
  completed,
  failed,
  onExpand,
}: {
  isCompleted: boolean;
  isFailed: boolean;
  progress: number;
  completed: number;
  failed: number;
  onExpand: () => void;
}) => {
  return (
    // 채널톡 데스크톱 런처(bottom:24 right:24, 54px) 왼쪽에 12px 간격으로 배치
    // → 위젯 오른쪽 끝 = 24 + 54 + 12 = 90px, 바닥 정렬 bottom-6(24px)
    <div className="fixed bottom-6 right-[90px] z-40">
      {/* 진행중↔완료 전환 시 카드 높이를 spring으로 morph(Apple Dynamic Island 기법):
          layout이 높이 변화를 애니메이션하고, AnimatePresence(popLayout)가 내용을 blur 크로스페이드.
          진행 위젯도 카드 전체를 클릭 대상으로 → 클릭 시 scale 축소(내부 '자세히'는 중첩 버튼 방지로 span). */}
      <motion.button
        type="button"
        onClick={onExpand}
        layout
        whileTap={{ scale: 0.96 }}
        transition={{
          layout: { type: 'spring', bounce: 0.28, duration: 0.55 },
          scale: { type: 'spring', stiffness: 500, damping: 30 },
        }}
        style={{ borderRadius: 24 }}
        className="flex w-[362px] cursor-pointer flex-col overflow-hidden border border-cds-warmgray-20 bg-white text-left shadow-[0px_3px_10px_rgba(0,0,0,0.1)]"
      >
        <AnimatePresence mode="popLayout" initial={false}>
          {isCompleted ? (
            <motion.span
              key="done"
              initial={{ opacity: 0, filter: 'blur(4px)', scale: 0.97 }}
              animate={{ opacity: 1, filter: 'blur(0px)', scale: 1, transition: { delay: 0.06 } }}
              exit={{ opacity: 0, filter: 'blur(4px)', scale: 0.97 }}
              transition={{ type: 'spring', bounce: 0.25, duration: 0.4 }}
              className="flex items-start justify-between pb-4 pl-[23px] pr-4 pt-4"
            >
              <span className="flex flex-col gap-2">
                <span className="text-[16px] font-semibold text-cds-coolgray-85">
                  AI 일괄 댓글 등록이 완료되었어요
                </span>
                <span className="flex items-center gap-2.5">
                  <span className="block h-[14px] w-[80px] rounded-full bg-gradient-to-r from-cds-orange-45 to-cds-orange-55" />
                  <span className="flex items-center gap-2 text-[14px]">
                    <span>
                      <span className="text-cds-coolgray-65">성공</span>{' '}
                      <b className="font-bold text-cds-blue-60">{completed}</b>
                    </span>
                    <span className="h-[12px] w-px bg-cds-coolgray-30" />
                    <span>
                      <span className="text-cds-coolgray-65">실패</span>{' '}
                      <b className="font-bold text-cds-red-60">{failed}</b>
                    </span>
                  </span>
                </span>
              </span>
              <span className="flex shrink-0 items-center gap-0.5 rounded-[12px] bg-cds-coolgray-15 py-[5px] pl-3 pr-2 text-[14px] font-medium text-cds-coolgray-75">
                자세히
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path
                    d="m5 3 4 4-4 4"
                    stroke="currentColor"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </motion.span>
          ) : isFailed ? (
            <motion.span
              key="failed"
              initial={{ opacity: 0, filter: 'blur(4px)', scale: 0.97 }}
              animate={{ opacity: 1, filter: 'blur(0px)', scale: 1, transition: { delay: 0.06 } }}
              exit={{ opacity: 0, filter: 'blur(4px)', scale: 0.97 }}
              transition={{ type: 'spring', bounce: 0.25, duration: 0.4 }}
              className="flex items-start justify-between pb-4 pl-[23px] pr-4 pt-4"
            >
              <span className="flex flex-col gap-1">
                <span className="text-[16px] font-semibold text-cds-coolgray-85">
                  등록 중 오류가 발생했어요
                </span>
                <span className="text-[13px] text-cds-coolgray-50">잠시 후 다시 시도해주세요</span>
              </span>
              <span className="flex items-center gap-0.5 rounded-[12px] bg-cds-coolgray-15 py-[5px] pl-3 pr-2 text-[14px] font-medium text-cds-coolgray-75">
                자세히
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path
                    d="m5 3 4 4-4 4"
                    stroke="currentColor"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </motion.span>
          ) : (
            <motion.span
              key="running"
              initial={{ opacity: 0, filter: 'blur(4px)', scale: 0.97 }}
              animate={{ opacity: 1, filter: 'blur(0px)', scale: 1, transition: { delay: 0.06 } }}
              exit={{ opacity: 0, filter: 'blur(4px)', scale: 0.97 }}
              transition={{ type: 'spring', bounce: 0.25, duration: 0.4 }}
              className="flex items-start justify-between pb-4 pl-[23px] pr-4 pt-4"
            >
              <span className="flex flex-col gap-2">
                {/* 진행 중: 피드("xxx님...") + 사이사이 진행률 타이틀(IslandFeed idle) */}
                <IslandFeedText
                  fallback="AI 일괄 댓글 등록이 시작되었어요"
                  className="text-[16px] font-semibold [--base-color:#35373E] [--base-gradient-color:#EA5400]"
                />
                <span className="flex items-center gap-1.5">
                  <span className="relative block h-[14px] w-[60px] overflow-hidden rounded-full bg-cds-opacity-8">
                    <span
                      className="absolute inset-y-0 left-0 block rounded-full bg-gradient-to-r from-cds-orange-45 to-cds-orange-55 transition-[width] duration-500 ease-out"
                      style={{ width: `${progress}%` }}
                    />
                  </span>
                  <span className="text-[14px]">
                    <b className="font-bold text-cds-orange-55 tabular-nums">
                      <NumberFlow
                        willChange
                        plugins={[continuous]}
                        value={Math.round(progress)}
                        suffix="%"
                      />
                    </b>{' '}
                    <span className="text-cds-coolgray-50">완료</span>
                  </span>
                </span>
              </span>
              <span className="flex items-center gap-0.5 rounded-[12px] bg-cds-coolgray-15 py-[5px] pl-3 pr-2 text-[14px] font-medium text-cds-coolgray-75">
                자세히
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path
                    d="m5 3 4 4-4 4"
                    stroke="currentColor"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>
    </div>
  );
};

export default DesktopProgressView;
